import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { validateChecklist } from '../utils/validation';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  Typography,
  Paper,
  Box,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import './ChecklistForm.css';

const initialFormData = {
  vehicle_number: '',
  driver: '',
  date: '',
  current_odometer: '',
  previous_odometer: '',
  miles_traveled: '',
  license_plate_number: '',
  oil_level: false,
  oil_level_notes: '',
  oil_change_last_date: '',
  oil_change_next_date: '',
  antifreeze_level: false,
  windshield_fluid: false,
  transmission_fluid: false,
  tire_pressure_lf: '',
  tire_pressure_rf: '',
  tire_pressure_lr: '',
  tire_pressure_rr: '',
  brake_fluid: false,
  tire_tread: false,
  fan_belt: false,
  headlights: false,
  taillights: false,
  brake_lights: false,
  running_lights: false,
  turn_signals: false,
  emergency_flashers: false,
  registration: false,
  registration_expiry: '',
  insurance: false,
  fire_extinguisher: false,
  fire_extinguisher_charged: false,
  fire_extinguisher_cert_date: '',
  safety_kit_flags: false,
  safety_kit_first_aid: false,
  safety_kit_eye_wash: false,
  safety_kit_eye_wash_expiry: '',
  safety_kit_flares: false,
  safety_kit_gloves: false,
  safety_kit_hard_hat: false,
  safety_kit_goggles: false,
  safety_kit_caution_tape: false,
  safety_kit_cones: false,
  safety_kit_harness: false,
  safety_kit_lumbar_support: false,
  tire_rotation_date: '',
  engine_tuneup_date: '',
  transmission_service_date: '',
  appearance: '',
  exterior_condition: false,
  interior_vacuumed: false,
  interior_condition: false,
  sanitizing_supplies: false,
  extension_cords: false,
  tire_traction_devices: false,
  notes: '',
  tech_signature: ''
};

const ChecklistForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hasLatestChecklist, setHasLatestChecklist] = useState(false);
  const [isOverride, setIsOverride] = useState(false);
  const [branchVehicles, setBranchVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [vehicleWarning, setVehicleWarning] = useState('');
  const [vehicleConflict, setVehicleConflict] = useState(null);

  useEffect(() => {
    const fetchLatestChecklist = async () => {
      try {
        const dashboardData = await apiService.getUserDashboard();
        if (dashboardData.latestChecklist) {
          setHasLatestChecklist(true);
          setFormData(prevState => ({
            ...prevState,
            previous_odometer: dashboardData.latestChecklist.current_odometer.toString(),
            // Auto-fill commonly unchanged fields from latest checklist
            license_plate_number: dashboardData.latestChecklist.license_plate_number || '',
            registration_expiry: dashboardData.latestChecklist.registration_expiry || '',
            fire_extinguisher_cert_date: dashboardData.latestChecklist.fire_extinguisher_cert_date || '',
            safety_kit_eye_wash_expiry: dashboardData.latestChecklist.safety_kit_eye_wash_expiry || '',
            oil_change_last_date: dashboardData.latestChecklist.oil_change_last_date || ''
          }));
        } else {
          setHasLatestChecklist(false);
        }
      } catch (error) {
        console.error('Error fetching latest checklist:', error);
        setHasLatestChecklist(false);
      }
    };

    const fetchVehicleConflictStatus = async () => {
      try {
        const response = await apiService.getVehicleConflictStatus();
        if (response?.hasConflict) {
          setVehicleConflict(response);
        }
      } catch (err) {
        console.error('Error fetching vehicle conflict status:', err);
      }
    };

    if (user) {
      setFormData(prevState => ({
        ...prevState,
        vehicle_number: user.vehicle_number || '',
        driver: user.username || ''
      }));
      fetchLatestChecklist();
      fetchVehicleConflictStatus();
    }
  }, [user]);

  useEffect(() => {
    const current = parseInt(formData.current_odometer) || 0;
    const previous = parseInt(formData.previous_odometer) || 0;
    const milesTraveled = Math.max(current - previous, 0);
    setFormData(prevState => ({
      ...prevState,
      miles_traveled: milesTraveled.toString()
    }));
  }, [formData.current_odometer, formData.previous_odometer]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOverrideToggle = async (e) => {
    const checked = e.target.checked;
    setIsOverride(checked);
    if (checked && user) {
      try {
        setLoadingVehicles(true);
        const branchName = user.active_branch || user.branch;
        const vehicles = await apiService.getBranchVehicles(branchName);
        setBranchVehicles(vehicles || []);
      } catch (err) {
        console.error('Error loading branch vehicles:', err);
        setBranchVehicles([]);
      } finally {
        setLoadingVehicles(false);
      }
    }
  };

  const handleVehicleSelect = (e) => {
    const selectedNumber = e.target.value;
    const selected = branchVehicles.find(v => v.number === selectedNumber);
    setFormData(prev => ({
      ...prev,
      vehicle_number: selectedNumber,
      license_plate_number: selected?.license_plate || prev.license_plate_number
    }));
    if (selected && user && selected.branch && (selected.branch !== (user.active_branch || user.branch))) {
      setVehicleWarning('Selected unit is from a different branch. Submission is allowed but will be marked as an override.');
    } else {
      setVehicleWarning('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateChecklist(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);
      try {
        // Calculate estimated next oil change date if last oil change date is provided
        let nextOilChangeDate = '';
        if (formData.oil_change_last_date) {
          const lastOilChangeDate = new Date(formData.oil_change_last_date);
          // Assuming 5,000 miles between oil changes
          const milesTraveled = parseInt(formData.miles_traveled) || 0;
          if (milesTraveled > 0) {
            // Calculate days until next oil change based on current rate of miles per day
            const currentDate = new Date(formData.date || new Date());
            const daysSinceLastOilChange = Math.max(1, Math.floor((currentDate - lastOilChangeDate) / (1000 * 60 * 60 * 24)));
            const milesPerDay = milesTraveled / daysSinceLastOilChange;
            
            if (milesPerDay > 0) {
              // Calculate days until next oil change (5000 mile interval)
              const daysUntilNextOilChange = Math.floor(5000 / milesPerDay);
              const nextDate = new Date(currentDate);
              nextDate.setDate(nextDate.getDate() + daysUntilNextOilChange);
              nextOilChangeDate = nextDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
            }
          }
        }

        const submitData = {
          ...formData,
          user_id: user.id,
          miles_traveled: parseInt(formData.miles_traveled),
          oil_change_next_date: nextOilChangeDate || formData.oil_change_next_date
        };
        await apiService.createChecklist(submitData);
        setSubmitMessage({ type: 'success', text: 'Checklist submitted successfully! Redirecting to dashboard...' });
        
        setTimeout(() => {
          navigate('/');
        }, 1500);

      } catch (error) {
        setSubmitMessage({ 
          type: 'error', 
          text: `Error submitting checklist: ${error.response?.data?.message || error.message}. Please check your input and try again.` 
        });
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setSubmitMessage({ type: 'error', text: 'Please correct the errors in the form.' });
    }
  };

  const renderTextField = (name, label, type = "text") => (
    <TextField
      fullWidth
      type={type}
      name={name}
      label={label}
      value={formData[name]}
      onChange={handleChange}
      error={!!errors[name]}
      helperText={errors[name]}
      variant="outlined"
      margin="normal"
      className="form-input"
    />
  );

  const renderAutoFillTextField = (name, label, type = "text") => {
    const isAutoFilled = hasLatestChecklist && formData[name];
    return (
      <TextField
        fullWidth
        type={type}
        name={name}
        label={label}
        value={formData[name]}
        onChange={handleChange}
        error={!!errors[name]}
        helperText={errors[name] || (isAutoFilled ? "Auto-filled from last checklist (editable)" : "")}
        variant="outlined"
        margin="normal"
        className="form-input"
        sx={isAutoFilled ? {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(25, 118, 210, 0.04)'
          }
        } : {}}
      />
    );
  };

  const renderCheckbox = (name, label) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={formData[name]}
          onChange={handleChange}
          name={name}
        />
      }
      label={label}
      className="form-checkbox"
    />
  );

  // If user has vehicle conflict, show blocked message
  if (vehicleConflict?.hasConflict) {
    return (
      <Paper className="checklist-form">
        <Typography variant="h4" component="h1" gutterBottom>
          Monthly Vehicle Maintenance Checklist
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Vehicle Inspections Blocked
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your vehicle unit number (<strong>{vehicleConflict.vehicleNumber}</strong>) is currently also assigned to <strong>{vehicleConflict.conflictUsername}</strong>.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please speak to your service manager to resolve this conflict. Vehicle inspections and maintenance invoices are blocked until this is resolved.
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/')}>
            Return to Dashboard
          </Button>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper className="checklist-form">
      <Typography variant="h4" component="h1" gutterBottom>
        Monthly Vehicle Maintenance Checklist
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box className="form-section">
          <Typography variant="h6" className="form-section-title">Vehicle Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={isOverride} onChange={handleOverrideToggle} />}
                label="I'm driving a different vehicle this month"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {isOverride ? (
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel id="unit-number-label">Unit Number</InputLabel>
                  <Select
                    labelId="unit-number-label"
                    id="unit-number"
                    name="vehicle_number"
                    value={formData.vehicle_number}
                    onChange={handleVehicleSelect}
                    label="Unit Number"
                  >
                    {loadingVehicles ? (
                      <MenuItem value="" disabled>Loading...</MenuItem>
                    ) : (
                      branchVehicles.map(v => (
                        <MenuItem key={v.number} value={v.number}>
                          {v.number}{v.license_plate ? ` — ${v.license_plate}` : ''}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  name="vehicle_number"
                  label="Unit Number"
                  value={formData.vehicle_number}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  className="form-input"
                  InputProps={{ readOnly: true }}
                />
              )}
            </Grid>
            {vehicleWarning && (
              <Grid item xs={12}>
                <Alert severity="warning">{vehicleWarning}</Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>{renderTextField("driver", "Driver")}</Grid>
            <Grid item xs={12} sm={6}>{renderTextField("date", "Date", "date")}</Grid>
            <Grid item xs={12} sm={6}>{renderAutoFillTextField("license_plate_number", "License Plate Number")}</Grid>
            <Grid item xs={12} sm={4}>{renderTextField("current_odometer", "Current Odometer", "number")}</Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                name="previous_odometer"
                label="Previous Odometer"
                value={formData.previous_odometer}
                onChange={handleChange}
                error={!!errors.previous_odometer}
                helperText={hasLatestChecklist ? 
                  errors.previous_odometer : 
                  (errors.previous_odometer || "Enter the previous odometer reading for your first checklist")}
                variant="outlined"
                margin="normal"
                className="form-input"
                InputProps={{
                  readOnly: hasLatestChecklist,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                name="miles_traveled"
                label="Miles Traveled"
                value={formData.miles_traveled}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
                margin="normal"
                className="form-input"
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" className="form-section-title">Fluid Levels</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {renderCheckbox("oil_level", "Oil Level")}
              {formData.oil_level && renderTextField("oil_level_notes", "Oil Level Notes")}
            </Grid>
            <Grid item xs={12} sm={6}>{renderAutoFillTextField("oil_change_last_date", "Last Oil Change Date", "date")}</Grid>
            <Grid item xs={12} sm={4}>{renderCheckbox("antifreeze_level", "Antifreeze Level")}</Grid>
            <Grid item xs={12} sm={4}>{renderCheckbox("windshield_fluid", "Windshield Washer Fluid")}</Grid>
            <Grid item xs={12} sm={4}>{renderCheckbox("transmission_fluid", "Transmission Fluid")}</Grid>
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" className="form-section-title">Tire Condition</Typography>
          <Grid container spacing={2}>
            {["lf", "rf", "lr", "rr"].map((position) => (
              <Grid item xs={12} sm={3} key={position}>
                {renderTextField(`tire_pressure_${position}`, `${position.toUpperCase()} Pressure`, "number")}
              </Grid>
            ))}
            <Grid item xs={12}>{renderCheckbox("tire_tread", "Tire Tread Adequate")}</Grid>
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" className="form-section-title">Vehicle Systems</Typography>
          <Grid container spacing={2}>
            {[
              "brake_fluid", "fan_belt", "headlights", "taillights", "brake_lights",
              "running_lights", "turn_signals", "emergency_flashers"
            ].map((item) => (
              <Grid item xs={12} sm={4} key={item}>
                {renderCheckbox(item, item.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '))}
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" className="form-section-title">Documentation</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {renderCheckbox("registration", "Registration")}
              {renderAutoFillTextField("registration_expiry", "Registration Expiry Date", "date")}
            </Grid>
            <Grid item xs={12} sm={6}>{renderCheckbox("insurance", "Insurance")}</Grid>
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" className="form-section-title">Safety Equipment</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>{renderCheckbox("fire_extinguisher", "Fire Extinguisher")}</Grid>
            <Grid item xs={12} sm={6}>{renderCheckbox("fire_extinguisher_charged", "Fire Extinguisher Charged")}</Grid>
            <Grid item xs={12} sm={6}>
              {renderAutoFillTextField("fire_extinguisher_cert_date", "Fire Extinguisher Cert Date", "date")}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Safety Kit</Typography>
            </Grid>
            {[
              "flags", "first_aid", "eye_wash", "flares", "gloves", "hard_hat",
              "safety_goggles", "caution_tape", "cones", "harness", "lumbar_support"
            ].map((item) => (
              <Grid item xs={12} sm={4} key={item}>
                {renderCheckbox(`safety_kit_${item}`, item === 'safety_goggles' ? 'Safety Goggles' : item.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '))}
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              {renderAutoFillTextField("safety_kit_eye_wash_expiry", "Eye Wash Expiry Date", "date")}
            </Grid>
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" className="form-section-title">Maintenance Dates</Typography>
          <Grid container spacing={2}>
            {["tire_rotation", "engine_tuneup", "transmission_service"].map((item) => (
              <Grid item xs={12} sm={4} key={item}>
                {renderTextField(`${item}_date`, `Last ${item.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Date`, "date")}
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" className="form-section-title">Vehicle Condition</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" margin="normal">
                <InputLabel id="appearance-label">Appearance</InputLabel>
                <Select
                  labelId="appearance-label"
                  id="appearance"
                  name="appearance"
                  value={formData.appearance}
                  onChange={handleChange}
                  label="Appearance"
                  error={!!errors.appearance}
                >
                  <MenuItem value="clean">Clean</MenuItem>
                  <MenuItem value="dirty">Dirty</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>{renderCheckbox("exterior_condition", "Exterior Condition Acceptable")}</Grid>
            <Grid item xs={12} sm={6}>{renderCheckbox("interior_vacuumed", "Interior Vacuumed")}</Grid>
            <Grid item xs={12} sm={6}>{renderCheckbox("interior_condition", "Interior Condition Acceptable")}</Grid>
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" className="form-section-title">Additional Items</Typography>
          <Grid container spacing={2}>
            {["sanitizing_supplies", "extension_cords", "tire_traction_devices"].map((item) => (
              <Grid item xs={12} sm={4} key={item}>
                {renderCheckbox(item, item.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '))}
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" className="form-section-title">Notes and Signature</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>{renderTextField("notes", "Additional Notes", "textarea")}</Grid>
            <Grid item xs={12}>{renderTextField("tech_signature", "Technician Signature")}</Grid>
          </Grid>
        </Box>

        {submitMessage && (
          <Alert severity={submitMessage.type} sx={{ mt: 2 }}>
            {submitMessage.text}
          </Alert>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Checklist'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default ChecklistForm;